import * as React from "react";
import { useEffect } from "react";
import { Box, Typography, Button, InputAdornment, useMediaQuery, useTheme, MenuItem, Collapse, IconButton } from "@mui/material";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { centered_flex_box, main_button, MainInput } from "../../app/components/Styles";
import { Table } from "antd";
import { getReturnRequests } from "../../app/store/actions/dataActions";
import { Search, FilterList, FilterListOff } from "@mui/icons-material";
import NewCard from "../../app/components/NewCard";
import moment from "moment";
import { useTranslation } from "react-i18next";
import SearchFilterBar, { FilterChips } from "../../app/glass/SearchFilterBar";

const ReturnRequests = ({ getReturnRequests, returnRequests, isLoading }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const [page, setPage] = React.useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = React.useState(parseInt(searchParams.get('pageSize')) || 10);
  const [search, setSearch] = React.useState(searchParams.get('search') || '');
  const [status, setStatus] = React.useState(searchParams.get('status') || '');
  const [showFilters, setShowFilters] = React.useState(false);

  useEffect(() => {
    const params = { page, limit: pageSize, ...(search && { search }), ...(status && { status }) };
    getReturnRequests(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, status]);

  const handleSearch = () => {
    setPage(1);
    const params = { page: 1, limit: pageSize, ...(search && { search }), ...(status && { status }) };
    getReturnRequests(params);
    setSearchParams({ page: '1', pageSize: pageSize.toString(), ...(search && { search }), ...(status && { status }) });
  };

  const handleStatusChange = (value) => {
    setPage(1);
    setStatus(value);
    const params = { page: 1, limit: pageSize, ...(search && { search }), ...(value && { status: value }) };
    getReturnRequests(params);
    setSearchParams({ page: '1', pageSize: pageSize.toString(), ...(search && { search }), ...(value && { status: value }) });
  };

  const handleTableChange = (pagination) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
    setSearchParams({ page: pagination.current.toString(), pageSize: pagination.pageSize.toString(), ...(search && { search }), ...(status && { status }) });
  };

  const statusLabels = {
    Pending: t("returnsSystem.status.pending"),
    Accepted: t("returnsSystem.status.accepted"),
    Rejected: t("returnsSystem.status.rejected"),
  };

  const columns = [
    {
      title: t("orders.orderNumber"),
      dataIndex: ['originalOrder', 'shopifyOrderNumber'],
    },
    {
      title: t("orders.customerName"),
      dataIndex: ['fulfillment', 'customer'],
    },
    {
      title: t("common.phone"),
      dataIndex: ['fulfillment', 'phone'],
    },
    {
      title: t("common.date"),
      dataIndex: 'createdAt',
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: t("common.status"),
      dataIndex: 'status',
      render: (status) => statusLabels[status] || status
    },
    {
      title: t("common.actions"),
      render: (_, { _id }) => (
        <Button size="small" sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem" }} onClick={() => navigate(`/returns-system/return-requests/${_id}`)}>{t("common.view")}</Button>
      )
    },
  ];

  const data = returnRequests?.docs?.map((row) => ({ ...row }));

  const statusOptions = ['Pending', 'Accepted', 'Rejected'];

  return (
    <div>
      <Box sx={{ width: "100%", paddingX: isMobile ? 2 : 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1 }}>
          <Typography fontWeight={"bold"} fontSize={20}>{t("returnsSystem.returnRequests.title")}</Typography>
          <Button sx={{ ...main_button }} onClick={() => navigate(-1)}>{t("common.back")}</Button>
        </Box>
        <FilterChips
          value={status}
          onChange={handleStatusChange}
          options={[['', t("orders.allStatuses")], ...statusOptions.map((s) => [s, statusLabels[s] || s])]}
        />
        <SearchFilterBar
          placeholder={t("orders.searchPlaceholder")}
          value={search}
          onChange={setSearch}
          onSearch={handleSearch}
          isMobile={isMobile}
        />
        <Box sx={{
          width: "100%",
          overflowX: "auto",
          "& .ant-table-wrapper": { width: "100%" },
          "& .ant-table": { minWidth: isMobile ? 300 : 900 },
          "& .ant-table-cell": { whiteSpace: isMobile ? "normal" : "nowrap" }
        }}>
          <Table
            columns={columns}
            dataSource={data}
            loading={isLoading}
            rowKey={(r) => r._id}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: returnRequests?.totalDocs,
            }}
            onChange={handleTableChange}
            scroll={{ x: isMobile ? 300 : 900 }}
          />
        </Box>
      </Box>
    </div>
  );
};

const mapStateToProps = (state) => ({
  returnRequests: state?.records?.returnRequests,
  isLoading: state?.records?.isLoading,
});

const mapDispatchToProps = { getReturnRequests };

export default connect(mapStateToProps, mapDispatchToProps)(ReturnRequests);
